import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowUp, Minus, ArrowDown } from 'lucide-react';

type Priority = 'urgent' | 'high' | 'normal' | 'low';

interface PriorityIndicatorProps {
  priority: string;
  variant?: 'badge' | 'dot' | 'border';
  showLabel?: boolean;
  size?: 'sm' | 'md';
}

const priorityConfig: Record<
  Priority,
  { color: string; bgColor: string; borderColor: string; dotColor: string; icon: React.ElementType; labelKey: string }
> = {
  urgent: {
    color: 'text-rose-700 dark:text-rose-400',
    bgColor: 'bg-rose-100 dark:bg-rose-900/30',
    borderColor: 'border-l-rose-500',
    dotColor: 'bg-rose-500',
    icon: AlertCircle,
    labelKey: 'purchasing.urgent',
  },
  high: {
    color: 'text-orange-700 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    borderColor: 'border-l-orange-500',
    dotColor: 'bg-orange-500',
    icon: ArrowUp,
    labelKey: 'purchasing.high',
  },
  normal: {
    color: 'text-blue-700 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    borderColor: 'border-l-blue-500',
    dotColor: 'bg-blue-500',
    icon: Minus,
    labelKey: 'purchasing.normal',
  },
  low: {
    color: 'text-slate-600 dark:text-slate-400',
    bgColor: 'bg-slate-100 dark:bg-slate-800/50',
    borderColor: 'border-l-slate-300',
    dotColor: 'bg-slate-400',
    icon: ArrowDown,
    labelKey: 'purchasing.low',
  },
};

export default function PriorityIndicator({
  priority,
  variant = 'badge',
  showLabel = true,
  size = 'md',
}: PriorityIndicatorProps) {
  const { t } = useTranslation();

  const config = priorityConfig[priority as Priority] || priorityConfig.normal;
  const Icon = config.icon;

  const sizeClasses = {
    sm: {
      badge: 'px-2 py-0.5 text-xs gap-1',
      icon: 'h-3 w-3',
      dot: 'h-2 w-2',
    },
    md: {
      badge: 'px-2.5 py-1 text-xs gap-1.5',
      icon: 'h-3.5 w-3.5',
      dot: 'h-2.5 w-2.5',
    },
  };

  if (variant === 'dot') {
    return (
      <div className="flex items-center gap-2">
        <span
          className={`${sizeClasses[size].dot} rounded-full ${config.dotColor} ${
            priority === 'urgent' ? 'animate-pulse' : ''
          }`}
        />
        {showLabel && (
          <span className={`text-xs font-medium ${config.color}`}>
            {t(config.labelKey)}
          </span>
        )}
      </div>
    );
  }

  if (variant === 'border') {
    // Returns the border class to be applied to parent
    return null;
  }

  // Badge variant (default)
  return (
    <span
      className={`inline-flex items-center font-medium rounded-md ${config.bgColor} ${config.color} ${sizeClasses[size].badge}`}
    >
      <Icon className={sizeClasses[size].icon} />
      {showLabel && <span>{t(config.labelKey)}</span>}
    </span>
  );
}

// Helper function to get border class for cards
export function getPriorityBorderClass(priority: string): string {
  const config = priorityConfig[priority as Priority];
  if (!config) return '';

  if (priority === 'urgent' || priority === 'high') {
    return `border-l-4 ${config.borderColor}`;
  }
  return '';
}
